/** @jsxImportSource @emotion/react */
import {EMPTY_USER_AR, UserAr} from './userAr';
import * as R from 'ramda';
import {ColumnConfig} from '../../layout/table/columnConfig';
import * as React from 'react';
import {PropValidator} from '../../forms/validation/propValidation';
import Table from '../../layout/table/Table';
import {UserRow} from './UserRow';
import {Resort} from '../../resortDetails/resort';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';
import { SerializedStyles } from '@emotion/react';


type Props = {
  roles: string[];
  users: UserAr[];
  isLoadingUsers?: boolean;
  validateUser: PropValidator<UserAr>;
  onCreateUser: (user: UserAr) => void;
  onUpdateUser: (user: UserAr, isUpdatingPassword: boolean) => void;
  onDeleteUser: (user: UserAr) => void;
  onBlockUser: (userId: string) => void;
  onUnblockUser: (userId: string) => void;
  onSortUsers: (property: keyof UserAr, asc: boolean) => UserAr[]
  rootCss?: SerializedStyles;
  userResorts: Resort[];
  allResorts: Resort[];
}

export const UserManagementTable = (props: Props) => {

  const resources = useSelector((state: IAppState) => state.locale);

  const makeSortBy = (property: keyof UserAr) => (asc: boolean, data: UserAr[]): UserAr[] => {
    return [R.head(data)! ].concat(props.onSortUsers(property, asc)) as unknown as UserAr[];  // Always keep the create new user row first 
  };

  const columnConfig: Array<ColumnConfig<UserAr>> = [
    { id: 1, title: getResource(resources, 'USER_MANAGEMENT_TABLE_FIRST_NAME'), onSort: makeSortBy('firstName') },
    { id: 2, title: getResource(resources, 'USER_MANAGEMENT_TABLE_LAST_NAME'), onSort: makeSortBy('lastName') },
    { id: 3, title: getResource(resources, 'USER_MANAGEMENT_TABLE_USERNAME'), onSort: makeSortBy('userName') },
    { id: 4, title: getResource(resources, 'USER_MANAGEMENT_TABLE_EMAIL'), onSort: makeSortBy('email')},
    { id: 5, title: getResource(resources, 'USER_MANAGEMENT_TABLE_ROLE'), onSort: makeSortBy('role') },
    { id: 6, title: getResource(resources, 'USER_MANAGEMENT_TABLE_RESORTS'), onSort: makeSortBy('resortLocations') },
    { id: 7, title: getResource(resources, 'USER_MANAGEMENT_TABLE_PASSWORD') },
    { id: 8, title: getResource(resources, 'USER_MANAGEMENT_TABLE_LAST_LOGIN'), onSort: makeSortBy('lastLogin') },
    { id: 9, title: '', onSort: makeSortBy('blocked'), defaultSortingColumn: true, defaultSortingDirection: 'asc' },
  ];

  const renderRow = (user: UserAr, index: number): React.ReactNode => <UserRow
    key={index === 0 ? `CreateUserRow` : `User ${user.userId}`}
    isCreateRow={index === 0}
    validateUser={props.validateUser}
    roles={props.roles}
    user={user || EMPTY_USER_AR}
    onCreateUser={props.onCreateUser}
    onUpdateUser={props.onUpdateUser}
    onDeleteUser={props.onDeleteUser}
    userResorts={props.userResorts}
    allResorts={props.allResorts}
    onBlockUser={props.onBlockUser}
    onUnblockUser={props.onUnblockUser}
  />;

  return (
      <Table
      key='userManagementTable'
      noDataMessage={getResource(resources, 'USER_MANAGEMENT_TABLE_NO_USERS')}
      isLoading={!!props.isLoadingUsers}
      columns={columnConfig}
      data={R.concat([ EMPTY_USER_AR ], props.users)}
      renderRow={renderRow}
      rootCss={props.rootCss}
      infoMsg={getResource(resources, 'USER_PASSWORD_FORMAT_VALIDATION')}
      infoKey='Password'
    />
  );
}